import type { LostSuperRuleSet } from "./types.js";

// lost-super ruleset for the ATO reporting cycle ending 30 June 2025 (the
// "2024-25" reporting year — the latest annual ATO release, published Oct 2025).
//
// The "prize" is the ATO POPULATION aggregate the triage anchors to. These are
// not per-user figures and not a calculation — they are the hero stat that
// grounds the hedged "you probably have some" framing in the ATO's own numbers.
// They reprice each ATO cycle: never edit this shipped file to update them —
// author a new rates-keyed file (e.g. fy2025-26.ts) for the next release and
// register it alongside this one, so historical re-runs resolve against the
// figures in force then.
//
// Canonical figures (verification ledger lost-super-finder-explainer-figures.md,
// all CITED 6/6, checked 2026-06-22):
//   #1 Total lost (fund-held) + ATO-held super (30 Jun 2025) = just over $18.9bn
//   #2 Lost/unclaimed accounts                               = ~7.3 million
//   #3 ATO's stated average amount                           = $2,590
//   #4 Reunited/paid out by ATO since 2022                   = ~$5.5bn / ~3.1m accounts
//   #6 (sibling ledger) Australians with 2+ accounts         = ~4 million (30 Jun 2024)
//
// HARD RULE: every CTA routes to ATO/myGov only — $0 affiliate by design. The
// no-destination-fund-handoff guard in calculate.ts enforces this in code.
export const fy2024_25: LostSuperRuleSet = {
  product: "AU06", // shared Product enum; lost-super sits in the super/HECS cluster.
  fy: "2024-25",
  effectiveFrom: "2024-07-01",
  effectiveTo: "2025-06-30",
  sources: {
    mediaRelease:
      "https://www.ato.gov.au/media-centre/19-billion-dollars-in-lost-and-unclaimed-super-is-some-of-it-yours",
    statistics:
      "https://www.ato.gov.au/about-ato/research-and-statistics/in-detail/super-statistics/super-accounts-data/super-data-lost-unclaimed-multiple-accounts-and-consolidations",
    trendSingleAccounts:
      "https://www.ato.gov.au/about-ato/research-and-statistics/in-detail/super-statistics/super-accounts-data/super-data-lost-unclaimed-multiple-accounts-and-consolidations/trend-towards-single-accounts",
    consolidate:
      "https://www.ato.gov.au/individuals-and-families/super-for-individuals-and-families/super/growing-and-keeping-track-of-your-super/keeping-track-of-your-super",
  },
  sourcesVerifiedAt: "2026-06-22",
  prize: {
    // "Just over $18.9 billion" as at 30 Jun 2025 (ATO). We encode the headline
    // round figure the ATO release states.
    totalDollars: 18_900_000_000,
    // "Just under 7.3 million accounts" (fund-held 339k + ATO-held 6.958m).
    accounts: 7_300_000,
    // ATO: "The average amount is $2,590."
    averageParcel: 2_590,
    // "Since 2022 … almost $5.5 billion … approximately 3.1 million accounts."
    reunitedSince2022Dollars: 5_500_000_000,
    reunitedSince2022Accounts: 3_100_000,
    // ~4 million Australians held 2+ accounts (30 Jun 2024, ATO trend page).
    multipleAccountHolders: 4_000_000,
    asAt: "2025-06-30",
  },
  // Likelihood bands map a coarse risk SCORE (see calculate.ts weights) onto a
  // hedged typical-case band. Authored to cover [0, ∞).
  bands: [
    { key: "low", label: "Lower than average", minScore: 0, maxScore: 2 },
    { key: "moderate", label: "Around average", minScore: 2, maxScore: 4 },
    { key: "elevated", label: "Higher than average", minScore: 4, maxScore: 6 },
    { key: "high", label: "Much higher than average", minScore: 6, maxScore: null },
  ],
  // Fee-drag default assumptions. All disclosed in the output. Conservative,
  // round, clearly-illustrative values — NOT a quote for any particular fund.
  //   - 5% annual net return: a common mid-range illustrative real-ish return.
  //   - $100/yr admin + $200/yr default insurance per duplicate: order-of-
  //     magnitude defaults consistent with the PC 2018 "excess admin fees +
  //     excess insurance premiums" mechanic (the $2.6bn/yr system-wide leak).
  feeDragDefaults: {
    annualReturn: 0.05,
    adminFeePerAccount: 100,
    insurancePremiumPerAccount: 200,
  },
};
